/**
 * CheckoutTierCard — in-app-checkout Phase 3 Session 3.7 rework.
 *
 * Previously (signup-billing-mvp Phase 5 Session 7): the CTA POSTed
 * `/api/billing/checkout-session` and redirected the browser to the hosted
 * Stripe Checkout URL. That redirect flow is RETIRED in Phase 3.
 *
 * Now: the CTA calls `onChoose(tier)` — an in-app navigation callback
 * supplied by `<CheckoutTierGrid>` — which mounts `<InAppCheckout>` inline.
 * No `window.location.href` redirect, no API call from this component.
 *
 * `contact_only` tiers (`monthly_price_usd === null` OR `contact_only ===
 * true`) render a "REQUEST BESPOKE" CTA that fires `onRequestBespoke(tier)`
 * — an in-app callback that routes to the `<BespokeAwaiting>` flow (POST
 * /api/billing/bespoke-request → awaiting confirmation). The old mailto path
 * is RETIRED per FIX 5 (Phase 3 /review BLOCKING finding).
 *
 * Compositional fidelity reference: `dashboard-arcanum/src/pages/PricingPage.tsx`
 * L31-164. Each section maps to:
 *   - MEMBERSHIP eyebrow (font-mono, accent, tracking)
 *   - Serif headline (tier name)
 *   - Italic tagline
 *   - Big serif $price / mo
 *   - Divider
 *   - Nested feature box with /-prefix bullets
 *   - CTA button — contextual per tier state
 *
 * Sentry: surface tag changed from `stripe-redirect` → `checkout-confirm`
 * (the hosted redirect path is retired; errors are now in-app confirm errors).
 *
 * `recommended` prop adds a subtle accent-tinted border + glow on the
 * first active tier per the cascade design.
 */

import clsx from 'clsx';

interface TierShape {
  id: string;
  display_name: string;
  tagline: string;
  monthly_price_usd: number | null;
  feature_bullets: string[];
  is_active: boolean;
  /** Present in the API response; not used by the card itself but included so
   *  callers with richer types (e.g. `TierResponseRow` with `sort_order`) can
   *  pass their tier objects without a type-compatibility error. */
  sort_order?: number;
  /** Explicit contact-only discriminator (in-app-checkout ADR 0001). Falls
   *  back to `monthly_price_usd === null` when absent (back-compat with the
   *  existing API shape that has no `contact_only` column yet). */
  contact_only?: boolean;
}

export interface CheckoutTierCardProps {
  tier: TierShape;
  recommended?: boolean;
  /**
   * Called when the user clicks the primary CTA for a paid tier.
   * The caller (`<CheckoutTierGrid>`) mounts `<InAppCheckout>` in response.
   * NOT called for contact-only tiers (those render the bespoke request flow).
   */
  onChoose: (tier: TierShape) => void;
  /**
   * Called when the user clicks "REQUEST BESPOKE" on a contact-only tier.
   * The caller mounts `<BespokeAwaiting>` in response (in-app flow, not mailto).
   * Required for contact-only tiers; ignored for paid tiers.
   */
  onRequestBespoke?: (tier: TierShape) => void;
}

/** US dollar formatting — `$499` (no cents on whole-dollar tier prices). */
function formatPrice(usd: number): string {
  return Number.isInteger(usd) ? `$${usd}` : `$${usd.toFixed(2)}`;
}

export function CheckoutTierCard({
  tier,
  recommended = false,
  onChoose,
  onRequestBespoke,
}: CheckoutTierCardProps) {
  // A tier is contact-only when either the explicit flag is set OR the price is
  // null (back-compat with pre-migration API that has no `contact_only` column).
  const isContactUs =
    tier.contact_only === true || tier.monthly_price_usd === null;

  return (
    <div
      data-testid={`checkout-tier-card-${tier.id}`}
      data-recommended={recommended ? 'true' : 'false'}
      className={clsx(
        'flex-1 min-w-0 max-w-[400px] flex flex-col gap-2 px-8 pt-3 pb-3',
        'bg-panel-bg border rounded-[3px]',
        recommended
          ? 'border-accent/40 shadow-[0_0_24px_rgba(0,200,83,0.08)]'
          : 'border-panel-border'
      )}
    >
      {/* MEMBERSHIP eyebrow — PricingPage L60-65 */}
      <div className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-accent">
        Membership
      </div>

      {/* Serif name — PricingPage L67-73 (size tightened from 30px for viewport-fit) */}
      <div className="font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.01em] text-text-bright">
        {tier.display_name}
      </div>

      {/* Italic tagline — PricingPage L75-80 */}
      <div className="font-body italic text-[15px] leading-[1.6] text-text">
        {tier.tagline}
      </div>

      {/* Price block — PricingPage L82-94 */}
      {isContactUs ? (
        <div className="flex items-baseline gap-1.5 mt-1">
          <div className="font-serif text-[34px] font-semibold leading-none tracking-[-0.02em] text-text-bright">
            Contact us
          </div>
        </div>
      ) : (
        <div className="flex items-baseline gap-1.5 mt-1">
          <div className="font-serif text-[34px] font-semibold leading-none tracking-[-0.02em] text-text-bright">
            {formatPrice(tier.monthly_price_usd as number)}
          </div>
          <div className="font-mono text-[13px] tracking-[0.04em] text-text-muted">
            / mo
          </div>
        </div>
      )}

      {/* Divider — PricingPage L100 */}
      <div className="h-px bg-panel-border" />

      {/* Feature box — PricingPage L102-128 (padding + gap + bullet leading tightened for viewport-fit) */}
      <div className="bg-bg border border-accent/10 rounded-[3px] px-5 pt-3 pb-3 flex flex-col gap-2">
        {tier.feature_bullets.map((bullet, i) => (
          <div key={i} className="flex gap-3 items-start">
            <div className="font-mono text-[13px] font-medium leading-[1.5] text-accent shrink-0">
              /
            </div>
            <div className="font-body text-[13px] leading-[1.5] text-text">
              {bullet}
            </div>
          </div>
        ))}
      </div>

      {/* CTA — PricingPage L140-161 (translated to native button + tokens).
          Contact-only tiers: in-app bespoke request flow (NOT mailto).
          Paid tiers: in-app checkout via onChoose callback (no redirect). */}
      {isContactUs ? (
        <button
          type="button"
          onClick={() => onRequestBespoke?.(tier)}
          data-testid={`checkout-tier-card-${tier.id}-bespoke-cta`}
          className={clsx(
            'mt-auto w-full py-3',
            'bg-accent border border-accent rounded-[3px]',
            'font-mono text-[0.78rem] font-medium uppercase tracking-[0.14em]',
            'text-void cursor-pointer hover:brightness-110 transition-[filter] duration-200'
          )}
        >
          Request Bespoke
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onChoose(tier)}
          data-testid={`checkout-tier-card-${tier.id}-cta`}
          className={clsx(
            'mt-auto w-full py-3',
            'bg-accent border border-accent rounded-[3px]',
            'font-mono text-[0.78rem] font-medium uppercase tracking-[0.14em]',
            'text-void cursor-pointer hover:brightness-110 transition-[filter] duration-200'
          )}
        >
          Choose {tier.display_name}
        </button>
      )}
    </div>
  );
}
